import { withSearch } from "@elastic/react-search-ui";
import type { FilterType, SearchResult } from "@elastic/search-ui";
import useCodeNavigate from "components/router/useCodeNavigate";
import {
  CodeSystem,
  ElasticsearchCodeSystems
} from "context/elasticsearch/types";
import { useStoreContext } from "context/StoreProvider";
import { Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import KisSearchResultRow from "./KisSearchResultRow";
import SearchResultRow from "./SearchResultRow";
import {
  ResultViewWrapper,
  SearchResultBar,
  TableHeader,
  TableHeaderContainer
} from "./style";

type CodeSystemProp = {
  codeSystem: CodeSystem;
  displayScore: boolean;
};

export type SearchResultTableProps = {
  searchTerm?: string;
  setFilter: (
    field: string,
    value: string,
    type: FilterType | undefined
  ) => void;
  results: SearchResult;
};

const SearchResultTable = ({
  results,
  searchTerm,
  setFilter,
  codeSystem,
  displayScore
}: SearchResultTableProps & CodeSystemProp) => {
  const navigate = useCodeNavigate();
  const { t } = useTranslation();

  const { actions, state } = useStoreContext();
  const { excludedSearchFields } = state;
  const { updateCodeValue } = actions;

  const isKis = codeSystem === CodeSystem.Adm;

  return (
    <SearchResultBar $isKis={isKis} role="table" className="position-relative">
      <TableHeaderContainer role="rowgroup">
        <TableHeader role="row" className="gap-1 gap-md-3 flex-nowrap">
          {isKis ? (
            <>
              <Col role="columnheader" className="p-0" xs={1}>
                <span>{t("general.id")}</span>
              </Col>
              <Col role="columnheader" xs={3} className="p-0">
                <span>{t("general.codesystem")}</span>
              </Col>
              <Col role="columnheader" xs={2} className="p-0">
                <span>{t("search.admTable.owner")}</span>
              </Col>
              <Col role="columnheader" xs={3} md={2} className="p-0">
                <span>{t("search.admTable.collection")}</span>
              </Col>
              <Col role="columnheader" xs={2} className="p-0">
                <span>{t("search.sorting.category")}</span>
              </Col>
              <Col role="columnheader" xs={1} className="text-end p-lg-0">
                <span>{t("search.admTable.status")}</span>
              </Col>
            </>
          ) : (
            <>
              {displayScore && (
                <Col role="columnheader" xs="auto" className="p-0">
                  <span>Score</span>
                </Col>
              )}
              <Col role="columnheader" xs="auto" className="p-0">
                <span>{t("general.codeValue")}</span>
              </Col>
              <Col role="columnheader" xs="auto" className="p-0">
                <span>{t("general.codeText")}</span>
              </Col>
            </>
          )}
        </TableHeader>
      </TableHeaderContainer>
      {results.map((result: SearchResult, idx: number) => {
        const shouldBeDisabled =
          codeSystem !== CodeSystem.Adm && // Never disabled for KIS and NORPAT
          codeSystem !== CodeSystem.Norpat &&
          result.active?.raw === false;

        const handleClickedResult = () => {
          if (isKis) {
            navigate.toCode(codeSystem, result.code_system_oid.raw);
            updateCodeValue(result.code_system_oid.raw);
          } else if (
            result.code_system.raw === ElasticsearchCodeSystems.Icpc2b
          ) {
            // ICPC-2B codes are shown under its parent ICPC-2 code
            navigate.toCode(codeSystem, result.parent_code.raw);
            updateCodeValue(result.parent_code.raw);
          } else if (codeSystem === CodeSystem.Icd10) {
            if (shouldBeDisabled) return null;
            navigate.toCode(codeSystem, result.code.raw);
            updateCodeValue(result.code.raw);
          } else {
            if (shouldBeDisabled) {
              return null;
            }
            navigate.toCode(codeSystem, result.code.raw.replace("_", ""));
            updateCodeValue(result.code.raw);
          }
        };

        const keyValue = isKis
          ? result.code_system_oid?.raw || `kis-${idx}`
          : result.code?.raw || `code-${idx}`;

        return (
          <ResultViewWrapper
            $isDisabled={shouldBeDisabled}
            onClick={handleClickedResult}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "Enter" || event.key === " ") {
                handleClickedResult();
              }
            }}
            action={!shouldBeDisabled}
            key={keyValue}
            role="rowgroup"
            className="w-100"
          >
            {isKis ? (
              <KisSearchResultRow
                result={result}
                searchTerm={searchTerm}
                showCodes={isKis && !excludedSearchFields.includes("code")}
                displayScore={displayScore}
              />
            ) : (
              <SearchResultRow
                result={result}
                searchTerm={searchTerm}
                setFilter={setFilter}
                displayScore={displayScore}
                codeSystem={codeSystem}
              />
            )}
          </ResultViewWrapper>
        );
      })}
    </SearchResultBar>
  );
};

export default withSearch<CodeSystemProp, SearchResultTableProps>(
  ({ searchTerm, results, setFilter }) => ({
    searchTerm,
    results,
    setFilter
  })
)(SearchResultTable);
